import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, Mail, Lock, Eye, EyeOff, ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

type PageState = 'loading' | 'available' | 'already_configured' | 'success';

const SetupPage = () => {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check whether an admin already exists by probing the edge function
    // We do a quick check via a dummy call to see if it 403s (already configured)
    const checkAdminExists = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ _probe: true }),
          }
        );
        if (res.status === 403) {
          setPageState('already_configured');
        } else {
          setPageState('available');
        }
      } catch {
        setPageState('available');
      }
    };
    checkAdminExists();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email, password }),
        }
      );
      const json = await res.json();
      if (!json.ok) {
        setError(json.error ?? 'Setup failed. Please try again.');
      } else {
        setPageState('success');
        // Auto sign-in the newly created admin
        await supabase.auth.signInWithPassword({ email, password });
        setTimeout(() => navigate('/'), 2000);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bolt-bg flex flex-col">
      {/* Minimal header */}
      <div className="px-6 py-4 border-b border-bolt-border">
        <Link to="/" className="flex items-center gap-2 group w-fit">
          <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center group-hover:bg-primary-500 transition-colors">
            <Zap className="h-4 w-4 text-white" fill="currentColor" />
          </div>
          <span className="text-bolt-text font-semibold text-sm">StackBlitz Team</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">

          {pageState === 'loading' && (
            <div className="text-center text-bolt-textMuted text-sm">Checking setup status...</div>
          )}

          {pageState === 'already_configured' && (
            <div className="card p-6 text-center space-y-4">
              <div className="w-10 h-10 rounded-full bg-success-500/15 flex items-center justify-center mx-auto">
                <ShieldCheck className="h-5 w-5 text-success-400" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-bolt-text mb-1">Already configured</h1>
                <p className="text-xs text-bolt-textMuted">
                  An admin account has already been set up. Sign in to continue.
                </p>
              </div>
              <Link to="/login" className="btn btn-primary w-full flex items-center justify-center gap-2">
                Go to sign in
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {pageState === 'success' && (
            <div className="card p-6 text-center space-y-4">
              <div className="w-10 h-10 rounded-full bg-success-500/15 flex items-center justify-center mx-auto">
                <CheckCircle className="h-5 w-5 text-success-400" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-bolt-text mb-1">Admin account created</h1>
                <p className="text-xs text-bolt-textMuted">Signing you in and redirecting to the directory...</p>
              </div>
            </div>
          )}

          {pageState === 'available' && (
            <>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-5 w-5 text-primary-400" />
                </div>
                <h1 className="text-xl font-semibold text-bolt-text mb-1">Initial setup</h1>
                <p className="text-sm text-bolt-textMuted">
                  Create the first admin account. This page is only available until an admin exists.
                </p>
              </div>

              <div className="card p-6">
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                    <input
                      type="email"
                      placeholder="Admin email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="form-input pl-9"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password (min 8 characters)"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="form-input pl-9 pr-10"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-bolt-textSubtle hover:text-bolt-textMuted transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="form-input pl-9"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>

                  {error && (
                    <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary w-full"
                  >
                    {isSubmitting ? 'Creating admin account...' : 'Create admin account'}
                  </button>
                </form>
              </div>

              <p className="mt-4 text-center text-xs text-bolt-textSubtle">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium transition-colors">
                  Sign in
                </Link>
              </p>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default SetupPage;
